<template>
  <transition name="apr-drawer-slide">
    <div v-if="visible" class="apr-drawer-backdrop" @click.self="$emit('close')">
      <aside class="apr-drawer">
        <!-- Vista: lista de recursos autorizados -->
        <template v-if="mode === 'list'">
          <header class="apr-drawer-header">
            <div>
              <h4>
                <b-icon pack="fas" icon="cloud" size="is-small" />
                {{ $t('autodesk.resources.title') }}
              </h4>
              <p class="apr-picker-hint" v-if="projectName">{{ projectName }}</p>
            </div>
            <button class="apr-drawer-close" @click="$emit('close')" :title="$t('autodesk.resources.close')">
              <b-icon pack="fas" icon="times" />
            </button>
          </header>

          <div class="apr-drawer-body">
            <div class="apr-list-actions">
              <button class="button is-primary is-small" @click="openPicker">
                <b-icon pack="fas" icon="plus" size="is-small" />
                <span>{{ $t('add_resource') }}</span>
              </button>
            </div>

            <div v-if="loading" class="apr-loading">
              <b-icon pack="fas" icon="spinner" custom-class="fa-spin" />
            </div>

            <div v-else-if="resources.length === 0" class="apr-empty">
              <b-icon pack="fas" icon="folder-open" size="is-large" />
              <p>{{ $t('autodesk.resources.empty') }}</p>
            </div>

            <ul v-else class="apr-list">
              <li v-for="r in topLevelResources" :key="r.id" class="apr-item">
                <div class="apr-item-icon">
                  <b-icon pack="fas" :icon="iconForUrn(r.resource_urn)" />
                </div>
                <div class="apr-item-body">
                  <div class="apr-item-name">{{ r.display_name || r.resource_urn }}</div>
                  <div class="apr-item-meta" v-if="r.parent_path">{{ r.parent_path }}</div>
                  <div v-if="!isViewable(r.resource_urn)" class="apr-item-count">
                    <b-icon pack="fas" icon="cube" size="is-small" />
                    <span>{{ $tc('autodesk.resources.models_inside', countModelsInside(r)) }}</span>
                  </div>
                </div>
                <button class="button is-small is-danger is-light" @click="removeResource(r)" :title="$t('autodesk.resources.remove')">
                  <b-icon pack="fas" icon="trash" size="is-small" />
                </button>
              </li>
            </ul>
          </div>
        </template>

        <!-- Vista: picker (árbol Autodesk) -->
        <template v-else-if="mode === 'picker'">
          <header class="apr-drawer-header">
            <div class="apr-header-with-back">
              <button class="apr-drawer-close" @click="mode = 'list'" :title="$t('back_to_list')">
                <b-icon pack="fas" icon="arrow-left" />
              </button>
              <div>
                <h4>{{ $t('autodesk.resources.picker_title') }}</h4>
                <p class="apr-picker-hint">{{ $t('autodesk.resources.picker_hint') }}</p>
              </div>
            </div>
            <button class="apr-drawer-close" @click="$emit('close')" :title="$t('autodesk.resources.close')">
              <b-icon pack="fas" icon="times" />
            </button>
          </header>

          <div class="apr-drawer-body">
            <div class="apr-picker-breadcrumb">
              <a v-for="(c, i) in crumbs" :key="i" @click="goCrumb(i)"
                 :class="{ 'is-current': i === crumbs.length - 1 }">
                <b-icon v-if="i === 0" pack="fas" icon="home" size="is-small" />
                {{ c.label }}
              </a>
            </div>

            <div v-if="pickerLoading" class="apr-loading">
              <b-icon pack="fas" icon="spinner" custom-class="fa-spin" />
            </div>

            <div v-else-if="pickerError" class="apr-picker-error">{{ pickerError }}</div>

            <div v-else-if="pickerItems.length === 0" class="apr-picker-empty">
              <b-icon pack="fas" icon="inbox" size="is-small" />
              <span>{{ $t('autodesk.resources.picker_empty') }}</span>
            </div>

            <ul v-else class="apr-picker-list">
              <li v-for="item in pickerItems" :key="item.id" class="apr-picker-item">
                <div class="apr-item-icon">
                  <b-icon pack="fas" :icon="iconForAcc(item)" />
                </div>
                <div class="apr-item-body" @click="navigateInto(item)"
                     :class="{ 'is-clickable': isNavigable(item) }">
                  <div class="apr-item-name">{{ item.attributes.displayName || item.attributes.name }}</div>
                  <div class="apr-item-meta" v-if="isNavigable(item)">
                    <b-icon pack="fas" icon="chevron-right" size="is-small" />
                    <span>{{ $t('autodesk.resources.navigate') }}</span>
                  </div>
                </div>
                <button v-if="isAddable(item)" class="button is-small is-success" @click="addResource(item)" :disabled="busy">
                  <b-icon pack="fas" :icon="isFolderLike(item) ? 'folder-plus' : 'plus'" size="is-small" />
                  <span>{{ $t('add') }}</span>
                </button>
              </li>
            </ul>
          </div>
        </template>
      </aside>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'
import store from '~/store'

export default {
  name: 'autodesk-project-resources',

  props: {
    visible: { type: Boolean, default: false },
    projectId: { type: [Number, String], required: true },
    projectName: { type: String, default: '' },
  },

  data: () => ({
    mode: 'list',       // 'list' | 'picker'
    loading: true,
    busy: false,
    resources: [],

    pickerLoading: false,
    pickerError: null,
    pickerItems: [],
    currentLevel: 'hubs',
    currentCtx: {},
    crumbs: [],
  }),

  computed: {
    // Admin solo ve lo que añadió explícitamente: carpetas/proyectos (siempre) +
    // items que NO son descendientes de otra carpeta/proyecto ya autorizado.
    topLevelResources() {
      const authorizedContainerNames = new Set(
        this.resources
          .filter(r => !this.isViewable(r.resource_urn))
          .map(r => r.display_name)
          .filter(Boolean)
      )
      return this.resources.filter(r => {
        if (!this.isViewable(r.resource_urn)) return true
        const parentSegments = (r.parent_path || '').split(' / ')
        return !parentSegments.some(seg => authorizedContainerNames.has(seg))
      })
    },
  },

  watch: {
    visible(open) {
      if (open) {
        this.mode = 'list'
        this.loadResources()
      }
    },
  },

  methods: {
    async loadResources() {
      this.loading = true
      try {
        const { data } = await axios.get(
          `/api/v2/autodesk/aps/projects/${this.projectId}/resources`,
          this.authHeader()
        )
        if (data.success) this.resources = data.data
      } finally {
        this.loading = false
      }
    },

    async removeResource(r) {
      if (!confirm(this.$t('autodesk.resources.confirm_remove'))) return
      try {
        await axios.delete(
          `/api/v2/autodesk/aps/projects/${this.projectId}/resources/${r.id}`,
          this.authHeader()
        )
        // Si r es un contenedor (folder/acc_project), limpiamos también los
        // items hijos que se habían auto-añadido. Match por nombre en parent_path.
        if (!this.isViewable(r.resource_urn) && r.display_name) {
          const descendants = this.resources.filter(x =>
            this.isViewable(x.resource_urn)
            && (x.parent_path || '').split(' / ').includes(r.display_name)
          )
          for (const d of descendants) {
            await axios.delete(
              `/api/v2/autodesk/aps/projects/${this.projectId}/resources/${d.id}`,
              this.authHeader()
            )
          }
        }
        await this.loadResources()
        this.toast(this.$t('autodesk.resources.toast_removed'))
      } catch (e) {
        this.toast(e.response?.data?.message || e.message || 'Error', 'is-danger')
      }
    },

    // ─── Picker ───
    openPicker() {
      this.mode = 'picker'
      this.resetPickerToHubs()
    },

    resetPickerToHubs() {
      this.currentLevel = 'hubs'
      this.currentCtx = {}
      this.crumbs = [{ label: this.$t('autodesk.browser.root'), level: 'hubs', ctx: {}, node: null }]
      this.loadHubs()
    },

    async loadHubs() {
      await this.pickerLoad('/api/v2/autodesk/aps/browse/hubs')
    },

    async pickerLoad(url, params = {}) {
      this.pickerLoading = true
      this.pickerError = null
      try {
        const { data } = await axios.get(url, { ...this.authHeader(), params })
        if (data.success) this.pickerItems = data.data || []
        else this.pickerError = data.message
      } catch (e) {
        this.pickerError = e.response?.data?.message || e.message
      } finally {
        this.pickerLoading = false
      }
    },

    navigateInto(item) {
      if (!this.isNavigable(item)) return
      const name = item.attributes.displayName || item.attributes.name

      if (item.type === 'hubs') {
        this.currentCtx = { hubId: item.id, hubRegion: item.attributes.region }
        this.crumbs.push({ label: name, level: 'projects', ctx: { ...this.currentCtx }, node: item })
        this.currentLevel = 'projects'
        this.pickerLoad('/api/v2/autodesk/aps/browse/projects', { hub_id: item.id })
      } else if (item.type === 'projects') {
        this.currentCtx = { ...this.currentCtx, projectId: item.id }
        this.crumbs.push({ label: name, level: 'folders', ctx: { ...this.currentCtx }, node: item })
        this.currentLevel = 'folders'
        this.pickerLoad('/api/v2/autodesk/aps/browse/top-folders', {
          hub_id: this.currentCtx.hubId, project_id: item.id,
        })
      } else if (item.type === 'folders') {
        this.currentCtx = { ...this.currentCtx, folderId: item.id }
        this.crumbs.push({ label: name, level: 'folders', ctx: { ...this.currentCtx }, node: item })
        this.pickerLoad('/api/v2/autodesk/aps/browse/folder-contents', {
          project_id: this.currentCtx.projectId, folder_id: item.id,
        })
      }
    },

    goCrumb(i) {
      const c = this.crumbs[i]
      this.crumbs = this.crumbs.slice(0, i + 1)
      this.currentCtx = { ...c.ctx }
      this.currentLevel = c.level
      if (c.level === 'hubs') this.loadHubs()
      else if (c.level === 'projects') {
        this.pickerLoad('/api/v2/autodesk/aps/browse/projects', { hub_id: c.ctx.hubId })
      } else if (c.level === 'folders') {
        if (c.ctx.folderId) {
          this.pickerLoad('/api/v2/autodesk/aps/browse/folder-contents', {
            project_id: c.ctx.projectId, folder_id: c.ctx.folderId,
          })
        } else {
          this.pickerLoad('/api/v2/autodesk/aps/browse/top-folders', {
            hub_id: c.ctx.hubId, project_id: c.ctx.projectId,
          })
        }
      }
    },

    async addResource(item) {
      this.busy = true
      try {
        const parentPath = this.crumbs.slice(1).map(c => c.label).join(' / ')
        const accProjectId = item.type === 'projects' ? item.id : this.currentCtx.projectId
        await this.persistResource(item, parentPath, accProjectId)

        // Al añadir carpeta/acc_project, también autorizamos sus items directos
        // porque el ACL check es match exacto por URN.
        if (this.isFolderLike(item)) {
          await this.addDirectChildItems(item, parentPath, 0, accProjectId)
        }
        await this.loadResources()
        const name = item.attributes.displayName || item.attributes.name
        this.toast(this.$t('autodesk.resources.toast_added', { name }))
        // Volver a la lista para que el admin vea el resultado.
        this.mode = 'list'
      } catch (e) {
        this.toast(e.response?.data?.message || e.message || 'Error', 'is-danger')
      } finally {
        this.busy = false
      }
    },

    async persistResource(item, parentPath, accProjectId) {
      // Para items (archivos) necesitamos el derivative URN (base64) que es lo
      // que el Viewer sabe cargar. El raw lineage URN hace que el viewer falle.
      let urnToSave = item.id
      if (item.type === 'items' && accProjectId) {
        const derivativeUrn = await this.resolveDerivativeUrn(accProjectId, item.id)
        if (derivativeUrn) urnToSave = derivativeUrn
      }
      await axios.post(
        `/api/v2/autodesk/aps/projects/${this.projectId}/resources`,
        {
          resource_urn: urnToSave,
          autodesk_region: this.currentCtx.hubRegion || 'EMEA',
          display_name: item.attributes.displayName || item.attributes.name,
          parent_path: parentPath,
        },
        this.authHeader()
      )
    },

    async resolveDerivativeUrn(accProjectId, itemUrn) {
      try {
        const { data } = await axios.get('/api/v2/autodesk/aps/browse/item-versions', {
          ...this.authHeader(),
          params: { project_id: accProjectId, item_id: itemUrn },
        })
        if (!data.success || !data.data?.length) return null
        return data.data[0].relationships?.derivatives?.data?.id || null
      } catch (e) {
        console.warn('No se pudo resolver derivative URN', itemUrn, e)
        return null
      }
    },

    // Recorre recursivamente carpeta/acc_project y persiste todos los items
    // (modelos) a cualquier profundidad. Subcarpetas intermedias NO se persisten
    // — así la lista queda limpia (admin ve carpeta raíz + items abribles).
    // Límite de profundidad para evitar explosiones en árboles muy profundos.
    async addDirectChildItems(parentItem, parentPath, depth, accProjectId) {
      const MAX_DEPTH = 6
      if (depth > MAX_DEPTH) return

      let url, params
      if (parentItem.type === 'projects') {
        url = '/api/v2/autodesk/aps/browse/top-folders'
        params = { hub_id: this.currentCtx.hubId, project_id: parentItem.id }
      } else {
        // Para folders usamos el accProjectId pasado por param — así funciona
        // también cuando el admin añade un ACC project desde el hub sin entrar
        // (currentCtx.projectId estaría vacío).
        url = '/api/v2/autodesk/aps/browse/folder-contents'
        params = { project_id: accProjectId, folder_id: parentItem.id }
      }
      try {
        const { data } = await axios.get(url, { ...this.authHeader(), params })
        if (!data.success) return
        const parentName = parentItem.attributes.displayName || parentItem.attributes.name
        // Dedupe: Autodesk suele poner un "top folder" con el mismo nombre que
        // el ACC project — no repetimos ese segmento en el path.
        const lastSegment = parentPath.split(' / ').pop()
        const childPath = (!parentPath || parentName === lastSegment)
          ? (parentPath || parentName)
          : `${parentPath} / ${parentName}`

        for (const child of (data.data || [])) {
          if (child.type === 'items') {
            await this.persistResource(child, childPath, accProjectId)
          } else if (child.type === 'folders') {
            // Descender en subcarpetas sin persistirlas (el usuario ve solo items).
            await this.addDirectChildItems(child, childPath, depth + 1, accProjectId)
          }
        }
      } catch (e) {
        console.warn('No se pudo expandir contenido de carpeta', e)
      }
    },

    isNavigable(item) {
      return ['hubs', 'projects', 'folders'].includes(item.type)
    },

    isAddable(item) {
      // Solo se autorizan carpetas o modelos concretos. Hubs y ACC projects son
      // contenedores demasiado amplios — el admin entra en ellos y pica la
      // granularidad real que quiere compartir.
      return ['folders', 'items'].includes(item.type)
    },

    isFolderLike(item) {
      return ['projects', 'folders'].includes(item.type)
    },

    isViewable(urn) {
      if (typeof urn !== 'string') return false
      if (/^[ab]\./.test(urn)) return false
      if (urn.includes('fs.folder')) return false
      if (urn.includes('dm.lineage')) return false
      return true
    },

    // Cuenta cuántos items hijos tiene este contenedor (según display_name
    // en parent_path de otros recursos). Para mostrar "N modelos dentro".
    countModelsInside(r) {
      const name = r.display_name
      if (!name) return 0
      return this.resources.filter(x =>
        this.isViewable(x.resource_urn)
        && (x.parent_path || '').split(' / ').includes(name)
      ).length
    },

    toast(message, type = 'is-success') {
      if (this.$buefy?.toast) {
        this.$buefy.toast.open({ message, type, duration: 2500, position: 'is-bottom' })
      }
    },

    iconForUrn(urn) {
      if (typeof urn !== 'string') return 'file'
      if (/^[ab]\./.test(urn)) return 'folder-tree'   // hub o ACC project
      if (urn.includes('fs.folder')) return 'folder'
      return 'file'
    },

    iconForAcc(item) {
      if (item.type === 'hubs') return 'building'
      if (item.type === 'projects') return 'folder-tree'
      if (item.type === 'folders') return 'folder'
      return 'file'
    },

    authHeader() {
      return { headers: { Authorization: 'Bearer ' + store.getters['auth/token'] } }
    },
  },
}
</script>

<style scoped>
/* Drawer lateral derecho */
.apr-drawer-backdrop {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 50;
  display: flex; justify-content: flex-end;
}
.apr-drawer {
  width: 560px; max-width: 90vw;
  height: 100vh;
  background: #fff;
  display: flex; flex-direction: column;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.1);
}
.apr-drawer-header {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 20px 20px 14px;
  border-bottom: 1px solid #eef1f6;
}
.apr-drawer-header h4 {
  font-size: 1.1rem; font-weight: 600; color: #2f374a; margin: 0;
  display: flex; align-items: center; gap: 8px;
}
.apr-header-with-back {
  display: flex; align-items: center; gap: 8px;
}
.apr-drawer-close {
  all: unset;
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%; cursor: pointer; color: #5d6b82;
  transition: background 0.15s;
  flex-shrink: 0;
}
.apr-drawer-close:hover { background: #f5f7fb; color: #2f374a; }
.apr-drawer-body {
  flex: 1; overflow-y: auto;
  padding: 16px 20px;
}

/* Slide-in desde la derecha */
.apr-drawer-slide-enter-active,
.apr-drawer-slide-leave-active { transition: opacity 0.2s ease; }
.apr-drawer-slide-enter-active .apr-drawer,
.apr-drawer-slide-leave-active .apr-drawer { transition: transform 0.25s ease-out; }
.apr-drawer-slide-enter, .apr-drawer-slide-leave-to { opacity: 0; }
.apr-drawer-slide-enter .apr-drawer, .apr-drawer-slide-leave-to .apr-drawer { transform: translateX(100%); }

.apr-list-actions { display: flex; justify-content: flex-end; margin-bottom: 12px; }
.apr-loading, .apr-empty { padding: 32px; text-align: center; color: #888; }
.apr-list { list-style: none; padding: 0; margin: 0; }
.apr-item, .apr-picker-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px; border-bottom: 1px solid #f0f0f0;
}
.apr-item-icon { color: #666; width: 28px; text-align: center; flex-shrink: 0; }
.apr-item-body { flex: 1; min-width: 0; }
.apr-item-body.is-clickable { cursor: pointer; }
.apr-item-body.is-clickable:hover { color: #3273dc; }
.apr-item-name { font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.apr-item-meta {
  font-size: 0.75rem; color: #999;
  display: flex; align-items: center; gap: 4px; margin-top: 2px;
}
.apr-item-count {
  display: inline-flex; align-items: center; gap: 4px;
  margin-top: 4px;
  padding: 2px 8px; border-radius: 10px;
  background: #e8f0fe; color: #3273dc;
  font-size: 0.75rem; font-weight: 500;
  width: fit-content;
}

.apr-picker-hint { color: #5d6b82; font-size: 0.85rem; margin: 4px 0 0; }
.apr-picker-breadcrumb {
  display: flex; gap: 6px; flex-wrap: wrap; margin: 0 0 12px; font-size: 0.9rem;
}
.apr-picker-breadcrumb a {
  cursor: pointer; color: #3273dc;
  display: flex; align-items: center; gap: 4px;
}
.apr-picker-breadcrumb a:not(:last-child)::after {
  content: '›'; color: #999; margin-left: 4px;
}
.apr-picker-breadcrumb a.is-current { color: #333; cursor: default; font-weight: 500; }

.apr-picker-list { list-style: none; padding: 0; margin: 0; }
.apr-picker-empty {
  padding: 24px; text-align: center; color: #888;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.apr-picker-error { padding: 12px; background: #fff5f5; color: #c53030; border-radius: 4px; }
</style>
